import dayjs from "dayjs"
import styles from "./About.module.scss"
import { ReactComponent as GermanySVG } from "./germanyRegulatory.svg"
import { ReactComponent as HeadquartersSVG } from "./headquarters.svg"
import { ReactComponent as InfoSVG } from "./info.svg"
import { ReactComponent as RefSVG } from "./ref.svg"
import { ReactComponent as SwitzerlandSVG } from "./switzerlandRegulatory.svg"
import { ReactComponent as UDISVG } from "./udi.svg"
import { ReactComponent as UnitedKingdomSVG } from "./ukRegulatory.svg"
import {
  PolicyViewerLink,
  PageTemplate,
} from "src/components"
import { usePolyglot } from "src/contexts"
import {
  useTermsOfUseConsentDocument,
  usePrivacyNoticeConsentDocument,
} from "src/hooks"

const polyglotPrefix = "pages.help_and_feedback.about."

export function About() {
  const polyglot = usePolyglot()
  const { data: termsOfUseConsentDocument } = useTermsOfUseConsentDocument()
  const { data: privacyNoticeConsentDocument } = usePrivacyNoticeConsentDocument()

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.about")}
      backRoutePath={"/help_and_feedback"}
    >
      <div className={styles.container}>
        <div className={styles.textContainer}>
          <div className={styles.appTitle}>
            {polyglot.t(`${polyglotPrefix}app_title`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}app_description`)}
          </div>
        </div>
        <hr className={styles.hr} />
        <div className={styles.textContainer}>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}disclaimer_one`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}disclaimer_two`)}
          </div>
        </div>
        <PolicyViewerLink
          policyDocument={termsOfUseConsentDocument}
          policyDocumentName="terms-of-use"
          className={styles.link}
        >
          {polyglot.t(`${polyglotPrefix}terms_of_service_link`)}
        </PolicyViewerLink>
        <PolicyViewerLink
          policyDocument={privacyNoticeConsentDocument}
          policyDocumentName="privacy-notice"
          className={styles.link}
        >
          {polyglot.t(`${polyglotPrefix}privacy_policy_link`)}
        </PolicyViewerLink>
        <a
          className={styles.link}
          href={"/acknowledgements.txt"}
          target="_blank"
          rel="noreferrer"
          id="acknowledgements"
        >
          {polyglot.t(`${polyglotPrefix}acknowledgements_link`)}
        </a>
        <hr className={styles.hr} />
        <div>
          <HeadquartersSVG className={styles.svg} />
          <div
            className={styles.company}
            id="company-address"
          >
            Tandem Diabetes Care, Inc
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}us_address.street`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}us_address.city`)}
          </div>
        </div>
        <div className={styles.helpRow}>
          <InfoSVG className={styles.infoSvg} />
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}help_url`)}
          </div>
        </div>
        <div className={styles.text}>
          {polyglot.t(
            `${polyglotPrefix}version.software`,
            {
              version: process.env.REACT_APP_SOFTWARE_VERSION,
            },
          )}
        </div>
        <div className={styles.helpRow}>
          <UDISVG
            className={styles.legalRegulatoryIcon}
            aria-label={polyglot.t(`${polyglotPrefix}version.udi_aria_label`)}
          />
          <div
            id="version-udi"
            className={styles.text}
          >
            {polyglot.t(
              `${polyglotPrefix}version.udi`,
              {
                udi: "00850018992619",
                version: process.env.REACT_APP_SOFTWARE_VERSION,
                date: process.env.REACT_APP_MANUFACTURING_DATE
                  ? dayjs(
                    process.env.REACT_APP_MANUFACTURING_DATE,
                    "YYYY-MM-DD",
                  ).format("YYMMDD")
                  : dayjs().format("YYMMDD"),
              },
            )}
          </div>
        </div>
        <div className={styles.helpRow}>
          <RefSVG
            className={styles.legalRegulatoryIcon}
            aria-label={polyglot.t(`${polyglotPrefix}version.catalog_aria_label`)}
          />
          <div
            id="version-catalog"
            className={styles.text}
          >
            {polyglot.t(
              `${polyglotPrefix}version.catalog`,
              {
                number: "1008181",
              },
            )}
          </div>
        </div>
        <div>
          <GermanySVG className={styles.svg} />
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}germany_address.recipient`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}germany_address.street`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}germany_address.city`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}germany_address.country`)}
          </div>
        </div>
        <div>
          <SwitzerlandSVG className={styles.svg} />
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}switzerland_address.recipient`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}switzerland_address.street`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}switzerland_address.city`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}switzerland_address.country`)}
          </div>
        </div>
        <div>
          <UnitedKingdomSVG className={styles.svg} />
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}uk_address.recipient`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}uk_address.street`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}uk_address.city`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}uk_address.postal`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}uk_address.country`)}
          </div>
        </div>
        <div>
          <div className={styles.australianSponsor}>
            {polyglot.t(`${polyglotPrefix}australia_address.name`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.recipient`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.floor`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.building`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.street`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.postal_and_city`)}
          </div>
          <div className={styles.text}>
            {polyglot.t(`${polyglotPrefix}australia_address.country`)}
          </div>
        </div>
      </div>
    </PageTemplate>
  )
}
